import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { api } from '@/api/client'
import { getApiErrorMessage } from '@/shared/utils/apiError'

export type NoteVisibility = 'PRIVATE' | 'SHARED' | 'DEPARTMENT' | 'ORGANIZATION'
export type NotePriority = 'LOW' | 'MEDIUM' | 'HIGH'
export type NoteColor = 'YELLOW' | 'PINK' | 'BLUE' | 'GREEN' | 'ORANGE' | 'PURPLE'
export type NotesView = 'all' | 'mine' | 'shared' | 'starred' | 'trash'

export interface NoteCollaborator {
  userId: { _id: string; name: string; email?: string; department?: string } | string
  access: 'VIEW' | 'EDIT'
}

export interface NoteFolderRef {
  _id: string
  name: string
  color?: string
}

export interface Note {
  _id: string
  title: string
  content: string
  organizationId: string
  authorId: { _id: string; name: string; email?: string; department?: string } | string
  projectId?: string | { _id: string; name: string; key?: string } | null
  templateId?: string
  folderId?: NoteFolderRef | string | null
  department?: string
  tags: string[]
  priority?: NotePriority | null
  color?: NoteColor
  isStarred: boolean
  isPinned: boolean
  visibility: NoteVisibility
  collaborators: NoteCollaborator[]
  reminder?: { enabled: boolean; at?: string; sentAt?: string }
  isPublished: boolean
  publishedAt?: string
  createdAt: string
  updatedAt: string
  deletedAt?: string
}

export interface NoteFolder {
  _id: string
  name: string
  color?: string
  department?: string
  createdBy: string
  noteCount: number
}

export interface NoteSidebarCounts {
  all: number
  mine: number
  shared: number
  starred: number
  trash: number
}

export interface NotesQuery {
  view?: NotesView
  search?: string
  folderId?: string
  tag?: string
  priority?: string
  department?: string
  dateRange?: 'all' | 'today' | 'week' | 'month'
  page?: number
  limit?: number
}

export interface NotesListResponse {
  data: Note[]
  pagination: { page: number; limit: number; total: number; totalPages: number }
  counts: NoteSidebarCounts
}

function toParams(params: Record<string, any>) {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value))
    }
  })
  return searchParams.toString()
}

export const notesApi = {
  list: async (params: NotesQuery = {}): Promise<NotesListResponse> => {
    return api.get<any>(`/notes?${toParams({ ...params })}`)
  },
  get: async (id: string): Promise<{ data: Note; canEdit: boolean }> => {
    return api.get<any>(`/notes/${id}`)
  },
  create: async (data: { title: string; content?: string; folderId?: string; tags?: string[]; priority?: NotePriority | null; visibility?: NoteVisibility }): Promise<Note> => {
    const res = await api.post<any>('/notes', data)
    return res.data
  },
  update: async (id: string, data: Record<string, any>): Promise<Note> => {
    const res = await api.patch<any>(`/notes/${id}`, data)
    return res.data
  },
  remove: async (id: string): Promise<void> => {
    await api.delete<any>(`/notes/${id}`)
  },
  restore: async (id: string): Promise<Note> => {
    const res = await api.patch<any>(`/notes/${id}/restore`, {})
    return res.data
  },
  deletePermanent: async (id: string): Promise<void> => {
    await api.delete<any>(`/notes/${id}/permanent`)
  },
  duplicate: async (id: string): Promise<Note> => {
    const res = await api.post<any>(`/notes/${id}/duplicate`)
    return res.data
  },
  addCollaborator: async (id: string, data: { userId: string; access: 'VIEW' | 'EDIT' }): Promise<Note> => {
    const res = await api.post<any>(`/notes/${id}/collaborators`, data)
    return res.data
  },
  removeCollaborator: async (id: string, userId: string): Promise<Note> => {
    const res = await api.delete<any>(`/notes/${id}/collaborators/${userId}`)
    return res.data
  },
  setReminder: async (id: string, data: { enabled: boolean; at?: string }): Promise<Note> => {
    const res = await api.patch<any>(`/notes/${id}/reminder`, data)
    return res.data
  },
  folders: {
    list: async (): Promise<NoteFolder[]> => {
      const res = await api.get<any>('/note-folders')
      return res.data
    },
    create: async (data: { name: string; color?: string }): Promise<NoteFolder> => {
      const res = await api.post<any>('/note-folders', data)
      return res.data
    },
    update: async (id: string, data: { name?: string; color?: string }): Promise<NoteFolder> => {
      const res = await api.patch<any>(`/note-folders/${id}`, data)
      return res.data
    },
    remove: async (id: string): Promise<void> => {
      await api.delete<any>(`/note-folders/${id}`)
    },
  },
}

export function useNotesList(params: NotesQuery) {
  return useQuery({
    queryKey: ['notes', 'list', params],
    queryFn: () => notesApi.list(params),
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  })
}

export function useNote(id: string | undefined) {
  return useQuery({
    queryKey: ['notes', 'detail', id],
    queryFn: () => notesApi.get(id!),
    enabled: !!id,
    staleTime: 10_000,
  })
}

export function useNoteFolders() {
  return useQuery({
    queryKey: ['note-folders'],
    queryFn: () => notesApi.folders.list(),
    staleTime: 30_000,
  })
}

function invalidateNotes(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['notes'] })
  queryClient.invalidateQueries({ queryKey: ['note-folders'] })
}

export function useCreateNote() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: notesApi.create,
    onSuccess: () => invalidateNotes(queryClient),
    onError: (err: any) => toast.error(getApiErrorMessage(err, 'create note')),
  })
}

export function useUpdateNote() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, any> }) => notesApi.update(id, data),
    onSuccess: (note) => {
      queryClient.setQueryData(['notes', 'detail', note._id], (prev: any) => (prev ? { ...prev, data: note } : prev))
      invalidateNotes(queryClient)
    },
    onError: (err: any) => toast.error(getApiErrorMessage(err, 'update note')),
  })
}

export function useDeleteNote() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: notesApi.remove,
    onSuccess: () => {
      invalidateNotes(queryClient)
      toast.success('Moved to trash')
    },
    onError: (err: any) => toast.error(getApiErrorMessage(err, 'delete note')),
  })
}

export function useRestoreNote() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: notesApi.restore,
    onSuccess: () => {
      invalidateNotes(queryClient)
      toast.success('Note restored')
    },
    onError: (err: any) => toast.error(getApiErrorMessage(err, 'restore note')),
  })
}

export function useDeleteNotePermanent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: notesApi.deletePermanent,
    onSuccess: () => {
      invalidateNotes(queryClient)
      toast.success('Note deleted permanently')
    },
    onError: (err: any) => toast.error(getApiErrorMessage(err, 'delete note')),
  })
}

export function useDuplicateNote() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: notesApi.duplicate,
    onSuccess: () => {
      invalidateNotes(queryClient)
      toast.success('Note duplicated')
    },
    onError: (err: any) => toast.error(getApiErrorMessage(err, 'duplicate note')),
  })
}

export function useNoteCollaborators() {
  const queryClient = useQueryClient()
  const add = useMutation({
    mutationFn: ({ id, userId, access }: { id: string; userId: string; access: 'VIEW' | 'EDIT' }) =>
      notesApi.addCollaborator(id, { userId, access }),
    onSuccess: (note) => {
      queryClient.setQueryData(['notes', 'detail', note._id], (prev: any) => (prev ? { ...prev, data: note } : prev))
      invalidateNotes(queryClient)
      toast.success('Collaborator added')
    },
    onError: (err: any) => toast.error(getApiErrorMessage(err, 'share note')),
  })
  const remove = useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) => notesApi.removeCollaborator(id, userId),
    onSuccess: (note) => {
      queryClient.setQueryData(['notes', 'detail', note._id], (prev: any) => (prev ? { ...prev, data: note } : prev))
      invalidateNotes(queryClient)
      toast.success('Collaborator removed')
    },
    onError: (err: any) => toast.error(getApiErrorMessage(err, 'remove collaborator')),
  })
  return { add, remove }
}

export function useNoteReminder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, enabled, at }: { id: string; enabled: boolean; at?: string }) => notesApi.setReminder(id, { enabled, at }),
    onSuccess: (note) => {
      queryClient.setQueryData(['notes', 'detail', note._id], (prev: any) => (prev ? { ...prev, data: note } : prev))
      invalidateNotes(queryClient)
      toast.success('Reminder saved — you will be notified')
    },
    onError: (err: any) => toast.error(getApiErrorMessage(err, 'save reminder')),
  })
}

export function useNoteFolderMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['note-folders'] })
    queryClient.invalidateQueries({ queryKey: ['notes'] })
  }
  const create = useMutation({
    mutationFn: notesApi.folders.create,
    onSuccess: () => {
      invalidate()
      toast.success('Folder created')
    },
    onError: (err: any) => toast.error(getApiErrorMessage(err, 'create folder')),
  })
  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; color?: string } }) => notesApi.folders.update(id, data),
    onSuccess: invalidate,
    onError: (err: any) => toast.error(getApiErrorMessage(err, 'rename folder')),
  })
  const remove = useMutation({
    mutationFn: notesApi.folders.remove,
    onSuccess: () => {
      invalidate()
      toast.success('Folder deleted')
    },
    onError: (err: any) => toast.error(getApiErrorMessage(err, 'delete folder')),
  })
  return { create, update, remove }
}

// ── Display helpers ──────────────────────────────────────────────────────────

export const DEPARTMENT_TAG_STYLES: Record<string, string> = {
  sales: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  crm: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  superadmin: 'bg-violet-50 text-violet-700 border-violet-200',
  admin: 'bg-violet-50 text-violet-700 border-violet-200',
  developer: 'bg-blue-50 text-blue-700 border-blue-200',
  engineering: 'bg-blue-50 text-blue-700 border-blue-200',
  it: 'bg-blue-50 text-blue-700 border-blue-200',
  marketing: 'bg-pink-50 text-pink-700 border-pink-200',
  hr: 'bg-orange-50 text-orange-700 border-orange-200',
  human: 'bg-orange-50 text-orange-700 border-orange-200',
  accounting: 'bg-teal-50 text-teal-700 border-teal-200',
  finance: 'bg-teal-50 text-teal-700 border-teal-200',
  general: 'bg-slate-100 text-slate-600 border-slate-200',
}

export function departmentTagStyle(department?: string) {
  const key = (department || 'general').toLowerCase()
  const match = Object.keys(DEPARTMENT_TAG_STYLES).find((k) => key.includes(k))
  return match ? DEPARTMENT_TAG_STYLES[match] : DEPARTMENT_TAG_STYLES.general
}

export const PRIORITY_LABELS: Record<NotePriority, string> = {
  HIGH: 'Priority: High',
  MEDIUM: 'Priority: Medium',
  LOW: 'Priority: Low',
}

export const VISIBILITY_LABELS: Record<NoteVisibility, string> = {
  PRIVATE: 'Private (Only you)',
  SHARED: 'Shared with selected users',
  DEPARTMENT: 'Department access',
  ORGANIZATION: 'Company-wide',
}

export const NOTE_COLORS: NoteColor[] = ['YELLOW', 'PINK', 'BLUE', 'GREEN', 'ORANGE', 'PURPLE']

// Sticky-note backgrounds for the board view, plus a solid swatch class.
export const STICKY_STYLES: Record<NoteColor, { bg: string; swatch: string }> = {
  YELLOW: { bg: 'bg-amber-100 border-amber-200', swatch: 'bg-amber-300' },
  PINK: { bg: 'bg-pink-100 border-pink-200', swatch: 'bg-pink-300' },
  BLUE: { bg: 'bg-sky-100 border-sky-200', swatch: 'bg-sky-300' },
  GREEN: { bg: 'bg-emerald-100 border-emerald-200', swatch: 'bg-emerald-300' },
  ORANGE: { bg: 'bg-orange-100 border-orange-200', swatch: 'bg-orange-300' },
  PURPLE: { bg: 'bg-violet-100 border-violet-200', swatch: 'bg-violet-300' },
}

export function initialsOf(name?: string) {
  if (!name) return '?'
  return name.split(' ').filter(Boolean).map((n) => n[0]).join('').slice(0, 2).toUpperCase()
}

export function authorName(note: Note, currentUserId?: string) {
  if (typeof note.authorId === 'string') {
    return note.authorId === currentUserId ? 'You' : 'Unknown'
  }
  if (note.authorId?._id && currentUserId && note.authorId._id === currentUserId) {
    return 'You'
  }
  return note.authorId?.name || 'Unknown'
}

export function formatNoteTime(iso?: string) {
  if (!iso) return ''
  const date = new Date(iso)
  const today = new Date()
  const isToday = date.toDateString() === today.toDateString()
  const yesterday = new Date(today.getTime() - 86400000).toDateString() === date.toDateString()
  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  if (isToday) return `Today, ${time}`
  if (yesterday) return `Yesterday, ${time}`
  return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
}

export function formatFullDateTime(iso?: string) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString([], {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}
